import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  UserPlus, Search, UserCheck, Stethoscope, 
  MapPin, Clock, ChevronRight, AlertCircle,
  Building2, Phone, Loader2, CheckCircle2
} from 'lucide-react';
import api from '../api/client';
import LocationAutocomplete from './LocationAutocomplete';

/**
 * Three-Tier Doctor Assignment Onboarding
 * 
 * Tier 1: Has existing provider → Send care request
 * Tier 2: Auto-assign (high-risk/underserved) → System assigns doctor
 * Tier 3: Patient choice → Browse and select doctor
 */
const DoctorAssignmentOnboarding = ({ 
  patientId, 
  onComplete, 
  onSkip,
  urgency = 'normal', // 'normal' | 'urgent'
  autoSkipIfAssigned = false // If true, auto-skip when user already has a provider
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro | has-provider | search | auto-assign | complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const completedRef = useRef(false); // Prevent multiple onComplete calls
  
  // Form states
  const [hasProvider, setHasProvider] = useState(null);
  const [providerSearch, setProviderSearch] = useState('');
  const [providerLocation, setProviderLocation] = useState({ city: '', state: '', zipCode: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [existingRequests, setExistingRequests] = useState([]); // Track existing requests to prevent duplicates
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [patientLocation, setPatientLocation] = useState({ city: '', state: '', zipCode: '' });

  // Check if already assigned and fetch existing requests
  useEffect(() => {
    checkAssignmentStatus();
    fetchExistingRequests();
  }, []);

  const checkAssignmentStatus = async () => {
    try {
      const response = await api.get('/doctor-assignment/check-status');
      if (response.data?.data?.hasProvider && autoSkipIfAssigned && !completedRef.current) {
        // Already has a provider, skip onboarding (only if autoSkipIfAssigned is true)
        completedRef.current = true;
        onComplete?.(response.data.data);
      }
    } catch (err) {
      console.error('Failed to check assignment status:', err);
    }
  };

  // Fetch existing requests to prevent duplicates
  const fetchExistingRequests = async () => {
    try {
      const response = await api.get('/care-requests/my-requests');
      setExistingRequests(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch existing requests:', err);
    }
  };

  // Check if already have pending request to this doctor
  const hasPendingRequest = (doctorId) => {
    return existingRequests.some(
      req => req.doctorId?._id === doctorId && req.status === 'pending'
    );
  };

  // Tier 1: Handle existing provider
  const handleHasProviderYes = () => {
    setHasProvider(true);
    setStep('search-provider');
  };

  const handleHasProviderNo = () => {
    setHasProvider(false);
    setStep('location-prompt');
  };

  // Search for existing provider
  const searchExistingProvider = async () => {
    if (!providerSearch.trim()) return;
    
    setLoading(true);
    try {
      const [searchResponse, requestsResponse] = await Promise.all([
        api.get('/doctor-assignment/search', {
          params: { 
            name: providerSearch,
            location: providerLocation.city,
            limit: 10
          }
        }),
        api.get('/care-requests/my-requests')
      ]);
      setSearchResults(searchResponse.data?.data || []);
      setExistingRequests(requestsResponse.data?.data || []);
    } catch (err) {
      setError('Failed to search for doctors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Request existing provider
  const requestExistingProvider = async (doctorId) => {
    setLoading(true);
    try {
      const response = await api.post('/doctor-assignment/request-doctor', {
        doctorId,
        message: `Patient is transferring care to your practice`
      });
      
      setAssignmentResult({
        type: 'existing',
        doctor: response.data?.data
      });
      
      toast.success('Care request sent successfully!');
      navigate('/care-requests');
      onComplete?.(response.data?.data);
    } catch (err) {
      setError('Failed to send care request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Tier 2: Auto-assign
  const handleAutoAssign = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/doctor-assignment/auto-assign', {
        location: patientLocation.city,
        urgency
      });
      
      if (response.data?.data?.success) {
        setAssignmentResult({
          type: 'auto',
          doctor: response.data?.data?.doctor
        });
        setStep('complete');
        onComplete?.(response.data?.data);
      } else {
        setError(response.data?.data?.message || 'No doctors available. Care coordinator will contact you.');
      }
    } catch (err) {
      setError('Failed to auto-assign doctor. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Tier 3: Patient choice - search doctors
  const handlePatientChoice = async () => {
    setStep('search-doctors');
    await searchAvailableDoctors();
  };

  const searchAvailableDoctors = async () => {
    setLoading(true);
    try {
      const response = await api.get('/doctor-assignment/search', {
        params: { 
          location: patientLocation.city,
          limit: 20
        }
      });
      setSearchResults(response.data?.data || []);
    } catch (err) {
      setError('Failed to load available doctors.');
    } finally {
      setLoading(false);
    }
  };

  const selectDoctor = async (doctor) => {
    setSelectedDoctor(doctor);
    await requestExistingProvider(doctor.id);
  };

  // Skip onboarding
  const handleSkip = () => {
    onSkip?.();
  };

  // Render steps
  const renderIntro = () => (
    <div className="text-center max-w-md mx-auto">
      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Stethoscope className="w-8 h-8 text-blue-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Welcome to SymptoConnect!
      </h2>
      
      <p className="text-slate-600 mb-8">
        To provide you with continuous care at home, we need to connect you with a healthcare provider.
      </p>

      {urgency === 'urgent' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Priority Assignment</span>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            Based on your health profile, we're prioritizing your provider assignment.
          </p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-slate-800 mb-4">
        Do you currently have a primary care provider?
      </h3>

      <div className="space-y-3">
        <button
          onClick={handleHasProviderYes}
          className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 
                     hover:bg-blue-50 transition-all text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Yes, I have a doctor</p>
              <p className="text-sm text-slate-500">Search for your existing provider</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
          </div>
        </button>

        <button
          onClick={handleHasProviderNo}
          className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 
                     hover:bg-blue-50 transition-all text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">No, I need a doctor</p>
              <p className="text-sm text-slate-500">We'll help you find one</p>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 ml-auto" />
          </div>
        </button>
      </div>

      <button
        onClick={handleSkip}
        className="mt-6 text-slate-400 hover:text-slate-600 text-sm"
      >
        Skip for now (you can do this later)
      </button>
    </div>
  );

  const renderSearchProvider = () => (
    <div className="max-w-lg mx-auto">
      <button 
        onClick={() => setStep('intro')}
        className="text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        ← Back
      </button>

      <h2 className="text-xl font-bold text-slate-900 mb-4">
        Find Your Doctor
      </h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Doctor's Name or Practice
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={providerSearch}
              onChange={(e) => setProviderSearch(e.target.value)}
              placeholder="Dr. Smith or City Medical Group"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg 
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && searchExistingProvider()}
            />
          </div>
        </div>

        <LocationAutocomplete
          label="Doctor's Location (optional)"
          value={providerLocation}
          onChange={setProviderLocation}
          placeholder="City or ZIP code"
        />

        <button
          onClick={searchExistingProvider}
          disabled={!providerSearch.trim() || loading}
          className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium
                     hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              Search
            </>
          )}
        </button>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-slate-700">
              {searchResults.length} doctors found
            </p>
            
            {searchResults.map((doctor) => {
              const isPending = hasPendingRequest(doctor.id);
              return (
                <button
                  key={doctor.id}
                  onClick={() => !isPending && selectDoctor(doctor)}
                  disabled={isPending}
                  className={`w-full p-3 border rounded-lg transition-all text-left ${
                    isPending 
                      ? 'border-amber-200 bg-amber-50 cursor-not-allowed' 
                      : 'border-slate-200 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      isPending ? 'bg-amber-100' : 'bg-blue-100'
                    }`}>
                      {isPending ? (
                        <Clock className="w-5 h-5 text-amber-600" />
                      ) : (
                        <Stethoscope className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{doctor.name}</p>
                        {isPending && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                            Request Pending
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{doctor.specialization}</p>
                      {doctor.location?.city && (
                        <p className="text-xs text-slate-400 mt-1">
                          {doctor.location.city}, {doctor.location.state}
                        </p>
                      )}
                    </div>
                    {!isPending && <ChevronRight className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderLocationPrompt = () => (
    <div className="max-w-md mx-auto">
      <button 
        onClick={() => setStep('intro')}
        className="text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        ← Back
      </button>

      <h2 className="text-xl font-bold text-slate-900 mb-4">
        Where are you located?
      </h2>

      <p className="text-slate-600 mb-6">
        This helps us find doctors near you who can provide in-person care if needed.
      </p>

      <LocationAutocomplete
        label="Your Location"
        value={patientLocation}
        onChange={setPatientLocation}
        placeholder="Enter your city or address"
        required
      />

      <div className="mt-6 space-y-3">
        <button
          onClick={handleAutoAssign}
          disabled={!patientLocation.city || loading}
          className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium
                     hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Finding best match...
            </>
          ) : (
            <>
              <UserPlus className="w-5 h-5" />
              Find Me a Doctor (Auto)
            </>
          )}
        </button>

        <button
          onClick={handlePatientChoice}
          disabled={!patientLocation.city || loading}
          className="w-full py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-medium
                     hover:bg-blue-50 disabled:border-slate-300 disabled:text-slate-400 
                     disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Search className="w-5 h-5" />
          Browse Available Doctors
        </button>
      </div>
    </div>
  );

  const renderSearchDoctors = () => (
    <div className="max-w-lg mx-auto">
      <button 
        onClick={() => setStep('location-prompt')}
        className="text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        ← Back
      </button>

      <h2 className="text-xl font-bold text-slate-900 mb-4">
        Available Doctors Near You
      </h2>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-2" />
          <p className="text-slate-500">Loading available doctors...</p>
        </div>
      ) : searchResults.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-slate-500">No doctors found in your area.</p>
          <button
            onClick={handleAutoAssign}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
          >
            Let us assign one for you
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            {searchResults.length} doctors available in {patientLocation.city}
          </p>
          
          {searchResults.map((doctor) => (
            <button
              key={doctor.id}
              onClick={() => selectDoctor(doctor)}
              className="w-full p-4 border border-slate-200 rounded-xl hover:border-blue-500 
                         hover:bg-blue-50 transition-all text-left"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-slate-500" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Dr. {doctor.name}</p>
                  <p className="text-sm text-slate-600">{doctor.specialization}</p>
                  
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                    {doctor.institution && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {doctor.institution}
                      </span>
                    )}
                    {doctor.location?.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {doctor.location.city}
                      </span>
                    )}
                    {doctor.availability?.acceptingNewPatients !== false && (
                      <span className="flex items-center gap-1 text-green-600">
                        <UserPlus className="w-3 h-3" />
                        Accepting new patients
                      </span>
                    )}
                  </div>

                  {doctor.bio && (
                    <p className="mt-2 text-sm text-slate-500 line-clamp-2">
                      {doctor.bio}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderComplete = () => (
    <div className="text-center max-w-md mx-auto">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <UserCheck className="w-10 h-10 text-green-600" />
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Doctor Assigned!
      </h2>

      <p className="text-slate-600 mb-6">
        {assignmentResult?.type === 'auto' 
          ? "We've matched you with a doctor based on your needs and their expertise."
          : "Your care request has been sent."}
      </p>

      {assignmentResult?.doctor && (
        <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">
                Dr. {assignmentResult.doctor.name}
              </p>
              <p className="text-sm text-slate-600">
                {assignmentResult.doctor.specialization}
              </p>
              {assignmentResult.doctor.institution && (
                <p className="text-sm text-slate-500">
                  {assignmentResult.doctor.institution}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4" />
              <span>Doctor will contact you within 24 hours</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone className="w-4 h-4" />
              <span>You can message them anytime in the app</span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => onComplete?.(assignmentResult)}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
      >
        Continue to Dashboard
      </button>

      <p className="mt-4 text-sm text-slate-500">
        A confirmation email has been sent with all the details.
      </p>
    </div>
  );

  const renderPending = () => (
    <div className="text-center max-w-md mx-auto">
      <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Clock className="w-10 h-10 text-amber-600" />
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-2">
        Request Sent
      </h2>

      <p className="text-slate-600 mb-6">
        We've sent a request to Dr. {selectedDoctor?.name}. 
        They'll need to accept before you can start sharing health information.
      </p>

      <div className="bg-slate-50 rounded-xl p-4 mb-6">
        <p className="text-sm text-slate-600">
          <strong>What's next?</strong>
        </p>
        <ul className="mt-2 text-sm text-slate-500 space-y-1 text-left">
          <li>• Doctor will review your request</li>
          <li>• You'll be notified when they accept</li>
          <li>• You can still use the app in the meantime</li>
        </ul>
      </div>

      <button
        onClick={() => onComplete?.(assignmentResult)}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
      >
        Continue to Dashboard
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${step === 'intro' ? 'bg-blue-600' : 'bg-green-500'}`} />
            <div className="w-8 h-0.5 bg-slate-200" />
            <div className={`w-3 h-3 rounded-full ${['search-provider', 'location-prompt', 'search-doctors'].includes(step) ? 'bg-blue-600' : 'bg-slate-200'}`} />
            <div className="w-8 h-0.5 bg-slate-200" />
            <div className={`w-3 h-3 rounded-full ${['complete', 'pending'].includes(step) ? 'bg-blue-600' : 'bg-slate-200'}`} />
          </div>
        </div>

        {/* Content */}
        {step === 'intro' && renderIntro()}
        {step === 'search-provider' && renderSearchProvider()}
        {step === 'location-prompt' && renderLocationPrompt()}
        {step === 'search-doctors' && renderSearchDoctors()}
        {step === 'complete' && renderComplete()}
        {step === 'pending' && renderPending()}
      </div>
    </div>
  );
};

export default DoctorAssignmentOnboarding;
